import { Button, Callout, Icon, Input, Link, Text } from "@appsmith/ads";
import {
  CREATE_NEW_ACCOUNT,
  createMessage,
  DONT_KNOW_YOUR_ORGANIZATION_URL,
  EMAIL_FAILED_TO_SEND,
  EMAIL_FAILED_TO_SEND_429,
  EMAIL_IS_INVALID,
  EMAIL_IS_REQUIRED,
  EMAIL_SENT_SUCCESSFULLY,
  EMAIL_SENT_SUCCESSFULLY_SUBTEXT,
  FIND_YOUR_ORGANIZATIONS,
  FIND_YOUR_ORGANIZATIONS_DESCRIPTION,
  LOOKING_TO_CREATE_NEW_ORG,
  ORG_SUB_DOMAIN_POSTFIX,
  ORG_SUB_DOMAIN_POSTFIX_FOR_AGENTS,
  RESEND_EMAIL,
  SEND_EMAIL_BUTTON_LABEL,
  SENDING,
  SIGN_IN_TO_ORG,
  SIGN_IN_TO_ORG_PAGE_SUBMIT_BUTTON,
  SIGN_IN_TO_ORG_PAGE_SUBTITLE,
  SIGN_IN_TO_ORG_PAGE_URL_PLACEHOLDER,
  YOU_VE_ALREADY_SIGNED_INTO,
} from "ee/constants/messages";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import AnalyticsUtil from "ee/utils/AnalyticsUtil";

import { useSelector } from "react-redux";

import * as Sentry from "@sentry/react";
import { getAssetUrl } from "ce/utils/airgapHelpers";
import { BASE_URL } from "constants/routes";
import { FEATURE_FLAG } from "ee/entities/FeatureFlag";
import { getIsAiAgentInstanceEnabled } from "ee/selectors/aiAgentSelectors";
import { getOrganizationConfig } from "ee/selectors/organizationSelectors";
import { getHTMLPageTitle } from "ee/utils/BusinessFeatures/brandingPageHelpers";
import { debounce } from "lodash";
import WelcomeBackground from "pages/setup/WelcomeBackground";
import Helmet from "react-helmet";
import { getIsSafeRedirectURL } from "utils/helpers";
import { useIsMobileDevice } from "utils/hooks/useDeviceDetect";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";
import Api from "api/Api";
import type { ApiResponse } from "api/types";
import {
  MainContainer,
  LeftPanel,
  RightPanel,
  FormContainer,
  FormHeader,
  FormFooter,
  FooterItem,
  SubmitForm,
  FindOrganizationsContainer,
  FindOrganizationsForm,
  FindOrganizationsDescription,
  SuccessMessageContainer,
  SuccessIcon,
  SuccessContent,
  SuccessMessage,
  CloseButton,
  ResendEmailLink,
} from "./styled";
import { getRecentDomains, isValidAppsmithDomain } from "utils/multiOrgDomains";

const OrgForm = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [isFindYourOrganizationsOpen, setIsFindYourOrganizationsOpen] =
    useState(false);
  const [findOrgEmail, setFindOrgEmail] = useState("");
  const [findOrgEmailError, setFindOrgEmailError] = useState("");
  const [isSubmittingFindOrg, setIsSubmittingFindOrg] = useState(false);
  const [isFindOrgEmailSent, setIsFindOrgEmailSent] = useState(false);

  let signupURL = BASE_URL;
  const redirectUrl = queryParams.get("redirectUrl");

  if (redirectUrl != null && getIsSafeRedirectURL(redirectUrl)) {
    const encodedRedirectUrl = encodeURIComponent(redirectUrl);

    signupURL += `?redirectUrl=${encodedRedirectUrl}`;
  }

  useEffect(function logEvent() {
    AnalyticsUtil.logEvent("SIGNUP_REACHED", {
      referrer: document.referrer,
    });
  }, []);

  const isBrandingEnabled = useFeatureFlag(
    FEATURE_FLAG.license_branding_enabled,
  );
  const organizationConfig = useSelector(getOrganizationConfig);
  const { instanceName } = organizationConfig;
  const htmlPageTitle = getHTMLPageTitle(isBrandingEnabled, instanceName);
  const [subdomain, setSubdomain] = useState("");
  const isAiAgentInstanceEnabled = useSelector(getIsAiAgentInstanceEnabled);
  const recentDomains = getRecentDomains();
  const isMobileDevice = useIsMobileDevice();

  let showError = false;
  let errorMessage = "";

  if (queryParams.get("error")) {
    errorMessage = queryParams.get("error") || "";
    showError = true;
    Sentry.captureException("Org sign selection failed", {
      level: "error",
      extra: {
        error: new Error(errorMessage),
      },
    });
  }

  const recentDomainsSection = recentDomains.length > 0 && (
    <div className="mt-8">
      <div className="mb-2">
        <Text kind="body-m">{createMessage(YOU_VE_ALREADY_SIGNED_INTO)}</Text>
      </div>

      <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md py-4 px-3">
        {recentDomains.map((domain, index) => {
          const orgName = domain
            .split(".")[0]
            .split("-")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");

          const avatarLetter = orgName.charAt(0).toUpperCase();

          const isLastItem = index === recentDomains.length - 1;

          return (
            <div
              className={`flex items-center justify-between p-1 ${
                isLastItem ? "mb-0" : "mb-3"
              }`}
              key={domain}
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-[color:var(--ads-color-background-secondary)] rounded-full flex items-center justify-center text-gray-600 font-light text-sm">
                  {avatarLetter}
                </div>
                <div className="flex flex-col">
                  <span className="text-md font-semibold text-gray-700 max-w-[180px] line-clamp-1">
                    {orgName}
                  </span>
                  <span className="text-xs font-light text-gray-500 max-w-[180px] line-clamp-1">
                    {domain}
                  </span>
                </div>
              </div>
              <Button
                className="px-4 py-2 text-sm"
                kind="secondary"
                onClick={() => {
                  if (isValidAppsmithDomain(domain)) {
                    window.location.href = `https://${domain}/user/login`;
                  }
                }}
                size="md"
              >
                Open
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    return emailRegex.test(email);
  };

  const handleFindOrgEmailChange = useCallback(
    (value: string) => {
      setFindOrgEmail(value);

      if (findOrgEmailError) {
        setFindOrgEmailError("");
      }
    },
    [findOrgEmailError],
  );

  const sendUserOrganizationsEmail = async (email: string) => {
    return Api.post("v1/users/organizations/send-email", null, {
      email,
    }) as unknown as ApiResponse<boolean>;
  };

  const handleFindOrgSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!findOrgEmail.trim()) {
        setFindOrgEmailError(createMessage(EMAIL_IS_REQUIRED));

        return;
      }

      const isEmailValid = validateEmail(findOrgEmail.trim());

      if (!isEmailValid) {
        setFindOrgEmailError(createMessage(EMAIL_IS_INVALID));

        return;
      }

      setIsSubmittingFindOrg(true);

      try {
        const response = await sendUserOrganizationsEmail(findOrgEmail.trim());

        if (!response.data) {
          setIsFindOrgEmailSent(false);
          setFindOrgEmailError(createMessage(EMAIL_FAILED_TO_SEND));
        }

        if (response.data) {
          setIsFindOrgEmailSent(true);
        } else if (response.responseMeta.status === 429) {
          setIsFindOrgEmailSent(false);
          setFindOrgEmailError(createMessage(EMAIL_FAILED_TO_SEND_429));
        }
      } catch (error) {
        setFindOrgEmailError(createMessage(EMAIL_FAILED_TO_SEND));
      } finally {
        setIsSubmittingFindOrg(false);
      }
    },
    [findOrgEmail],
  );

  const handleResendEmail = useCallback(async () => {
    if (isSubmittingFindOrg) return;

    setIsSubmittingFindOrg(true);

    try {
      const response = await sendUserOrganizationsEmail(findOrgEmail);

      if (!response.data) {
        setIsFindOrgEmailSent(false);
        setFindOrgEmailError(createMessage(EMAIL_FAILED_TO_SEND));
      }

      if (response.data) {
        setIsFindOrgEmailSent(true);
      } else if (response.responseMeta.status === 429) {
        setIsFindOrgEmailSent(false);
        setFindOrgEmailError(createMessage(EMAIL_FAILED_TO_SEND_429));
      }
    } catch (error) {
      setIsFindOrgEmailSent(false);
      setFindOrgEmailError(createMessage(EMAIL_FAILED_TO_SEND));
    } finally {
      setIsSubmittingFindOrg(false);
    }
  }, [findOrgEmail, isSubmittingFindOrg]);

  const handleCloseSuccessMessage = useCallback(() => {
    setIsFindOrgEmailSent(false);
    setIsFindYourOrganizationsOpen(false);
    setFindOrgEmail("");
  }, []);

  const renderFindYourOrganizationsForm = () => (
    <FindOrganizationsContainer>
      {isFindOrgEmailSent ? (
        <SuccessMessageContainer>
          <SuccessIcon>
            <Icon color="white" name="check-line" size="md" />
          </SuccessIcon>
          <SuccessContent>
            <SuccessMessage>
              {createMessage(EMAIL_SENT_SUCCESSFULLY)} <br />
              {createMessage(EMAIL_SENT_SUCCESSFULLY_SUBTEXT)}
            </SuccessMessage>
            <ResendEmailLink
              disabled={isSubmittingFindOrg}
              onClick={handleResendEmail}
              type="button"
            >
              {isSubmittingFindOrg
                ? createMessage(SENDING)
                : createMessage(RESEND_EMAIL)}
            </ResendEmailLink>
          </SuccessContent>
          <CloseButton
            aria-label="Close"
            onClick={handleCloseSuccessMessage}
            type="button"
          >
            ×
          </CloseButton>
        </SuccessMessageContainer>
      ) : (
        <>
          <FindOrganizationsForm onSubmit={handleFindOrgSubmit}>
            <div style={{ flex: 1 }}>
              <Input
                autoFocus
                errorMessage={findOrgEmailError}
                isInvalid={!!findOrgEmailError}
                name="findOrgEmail"
                onChange={handleFindOrgEmailChange}
                placeholder="your_email@email.com"
                size="md"
                type="text"
                value={findOrgEmail}
              />
            </div>
            <Button
              isDisabled={!findOrgEmail.trim() || isSubmittingFindOrg}
              isLoading={isSubmittingFindOrg}
              kind="secondary"
              size="md"
              type="submit"
            >
              {createMessage(SEND_EMAIL_BUTTON_LABEL)}
            </Button>
          </FindOrganizationsForm>

          <FindOrganizationsDescription>
            {createMessage(FIND_YOUR_ORGANIZATIONS_DESCRIPTION)}
          </FindOrganizationsDescription>
        </>
      )}
    </FindOrganizationsContainer>
  );

  const renderFooter = () => (
    <FormFooter>
      <FooterItem>
        {createMessage(DONT_KNOW_YOUR_ORGANIZATION_URL)}&nbsp;
        <Link
          className="t--find-your-organizations"
          kind="primary"
          onClick={() => setIsFindYourOrganizationsOpen(true)}
          target="_self"
        >
          {createMessage(FIND_YOUR_ORGANIZATIONS)}
        </Link>
      </FooterItem>

      {isFindYourOrganizationsOpen && renderFindYourOrganizationsForm()}

      <FooterItem>
        {createMessage(LOOKING_TO_CREATE_NEW_ORG)}&nbsp;
        <Link
          className="t--create-new-account"
          kind="primary"
          target="_self"
          to={signupURL}
        >
          {createMessage(CREATE_NEW_ACCOUNT)}
        </Link>
      </FooterItem>
    </FormFooter>
  );

  const debouncedCheckSubDomain = useMemo(
    () =>
      debounce((value: string) => {
        const updatedValue = value.replace(/^-+|-+$/g, "");

        setSubdomain(updatedValue);
      }, 500),
    [],
  );

  const handleChange = useCallback((value: string) => {
    //filter subdomain as per url standards
    value = value.toLowerCase().replace(/\W+/g, "-");
    setSubdomain(value);
    debouncedCheckSubDomain(value);
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const url = new URL(window.location.href);

      url.hostname = `${subdomain}.${url.hostname.split(".").slice(1).join(".")}`;
      // remove the path from the url
      url.pathname = "";
      window.location.href = url.toString();
    },
    [subdomain],
  );

  return (
    <>
      <Helmet>
        <title>{htmlPageTitle}</title>
      </Helmet>
      <MainContainer>
        <LeftPanel>
          <FormContainer>
            {!isMobileDevice && (
              <div>
                <img
                  className="h-8"
                  src={getAssetUrl(organizationConfig.brandLogoUrl)}
                />
              </div>
            )}
            <FormHeader>
              <div className="flex flex-col gap-2 text-left">
                <h1 className="text-[24px] font-semibold text-left text-[color:var(--ads-v2-color-fg-emphasis-plus)]">
                  {createMessage(SIGN_IN_TO_ORG)}
                </h1>
                <p className="text-[14px] text-left text-[color:var(--ads-v2\-color-fg)] whitespace-pre-line">
                  {createMessage(SIGN_IN_TO_ORG_PAGE_SUBTITLE)}
                </p>
              </div>
            </FormHeader>

            {showError && <Callout kind="error">{errorMessage}</Callout>}

            <SubmitForm onSubmit={handleSubmit}>
              <Input
                autoFocus
                name="subdomain"
                onChange={handleChange}
                placeholder={createMessage(SIGN_IN_TO_ORG_PAGE_URL_PLACEHOLDER)}
                postfix={
                  isAiAgentInstanceEnabled
                    ? createMessage(ORG_SUB_DOMAIN_POSTFIX_FOR_AGENTS)
                    : createMessage(ORG_SUB_DOMAIN_POSTFIX)
                }
                size="md"
                type="text"
                value={subdomain}
              />
              <Button
                isDisabled={!subdomain}
                kind="primary"
                size="md"
                type="submit"
              >
                {createMessage(SIGN_IN_TO_ORG_PAGE_SUBMIT_BUTTON)}
              </Button>
            </SubmitForm>

            {renderFooter()}

            {recentDomainsSection}
          </FormContainer>
        </LeftPanel>
        <RightPanel>
          <WelcomeBackground />
        </RightPanel>
      </MainContainer>
    </>
  );
};

export default OrgForm;
